"use client";

import type { AnnualSavings } from "@/lib/pricingDisplay";
import { formatZarAmount, savingsAriaLabel } from "@/lib/pricingDisplay";

type CheckVariant = "teal" | "navy";

/** Landing page: accessible checklist with visible checkmarks (no raw bullet clutter). */
export function LandingFeatureChecklist({
  features,
  checkVariant,
}: {
  features: readonly string[];
  checkVariant: CheckVariant;
}) {
  const checkColor = checkVariant === "teal" ? "#1DB8C4" : "#111E35";
  return (
    <ul
      role="list"
      style={{ margin: "0 0 22px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}
    >
      {features.map((f) => (
        <li
          key={f}
          role="listitem"
          style={{ display: "flex", gap: 10, fontSize: 13, color: "#1B2B4A", lineHeight: 1.45 }}
        >
          <span style={{ color: checkColor, fontWeight: 700, flexShrink: 0 }} aria-hidden>
            ✓
          </span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
  );
}

/** App pricing: compact disc list for dense feature text. */
export function AppFeatureBulletList({ features, color }: { features: readonly string[]; color: string }) {
  return (
    <ul
      role="list"
      style={{ margin: "0 0 1.15rem", paddingLeft: "1.1rem", color, fontSize: "0.86rem", lineHeight: 1.55 }}
    >
      {features.map((f) => (
        <li key={f} role="listitem" style={{ marginBottom: "0.45rem" }}>
          {f}
        </li>
      ))}
    </ul>
  );
}

/** Shared annual savings line (visual + aria). */
export function SavingsSubline({
  yearlyZar,
  save,
  mutedColor,
  tealColor,
  navyColor,
  fontSize = 12,
}: {
  yearlyZar: number;
  save: Pick<AnnualSavings, "savedZar" | "savedPct">;
  mutedColor: string;
  tealColor: string;
  navyColor: string;
  fontSize?: number;
}) {
  return (
    <p
      style={{ fontSize, color: mutedColor, marginBottom: 10, lineHeight: 1.45 }}
      aria-label={savingsAriaLabel(yearlyZar, save)}
    >
      or <strong style={{ color: navyColor }}>{formatZarAmount(yearlyZar)} / year</strong>
      <span style={{ color: tealColor, fontWeight: 600 }}>
        {" "}
        — keep ~{formatZarAmount(save.savedZar)} vs twelve monthly bills (~{save.savedPct}%)
      </span>
    </p>
  );
}
